import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, ChevronDown, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { FadeUp, Eyebrow } from "@/components/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
    "Ports & terminals",
    "Maritime & shipping",
    "Logistics",
    "Cold chain",
    "Pharma supply chains",
    "Energy transition",
    "Other",
];

const EMPTY = { name: "", organization: "", email: "", sector: "", message: "" };

const inputCls =
    "w-full border border-ink/15 bg-paper px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-inkmut focus:border-olive";

const Label = ({ children, testid }) => (
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-inksoft">
        {children}
        <span data-testid={testid} className="hidden" />
    </span>
);

const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/inquiries`, form);
            toast.success("Request received — Everlast Partners will be in touch.");
            setForm(EMPTY);
        } catch {
            toast.error("Something went wrong — please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
            <Toaster position="bottom-right" />
            <div className="container-x grid gap-14 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <FadeUp>
                        <Eyebrow>Contact</Eyebrow>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                            Request an{" "}
                            <em className="italic text-olive">introduction</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <p className="mt-6 max-w-md text-lg leading-relaxed text-inksoft">
                            Tell us a little about your institution and the
                            infrastructure on the table — we respond personally.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.3}>
                        <p
                            data-testid="contact-office-address"
                            className="mt-10 flex items-start gap-3 font-mono text-[11px] uppercase leading-relaxed tracking-[0.22em] text-inksoft"
                        >
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-olive" />
                            <span>
                                Office — 50 Milk Street, 16th Floor
                                <br />
                                Boston, MA 02109
                            </span>
                        </p>
                    </FadeUp>
                </div>

                <div className="lg:col-span-7">
                    <FadeUp delay={0.15}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="border border-ink/10 bg-white/70 p-6 backdrop-blur-sm sm:p-10"
                        >
                            <div className="grid gap-6 sm:grid-cols-2">
                                <label className="block">
                                    <Label testid="contact-label-name">Full name</Label>
                                    <input
                                        data-testid="contact-input-name"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        className={inputCls}
                                    />
                                </label>
                                <label className="block">
                                    <Label testid="contact-label-organization">
                                        Organization / title
                                    </Label>
                                    <input
                                        data-testid="contact-input-organization"
                                        value={form.organization}
                                        onChange={set("organization")}
                                        placeholder="Institution or role"
                                        className={inputCls}
                                    />
                                </label>
                                <label className="block">
                                    <Label testid="contact-label-email">
                                        Email address
                                    </Label>
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="name@institution.com"
                                        className={inputCls}
                                    />
                                </label>
                                <label className="block">
                                    <Label testid="contact-label-sector">
                                        Sector focus
                                    </Label>
                                    <span className="relative block">
                                        <select
                                            data-testid="contact-input-sector"
                                            value={form.sector}
                                            onChange={set("sector")}
                                            className={`${inputCls} appearance-none pr-10`}
                                        >
                                            <option value="">Select a sector</option>
                                            {SECTORS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-inkmut" />
                                    </span>
                                </label>
                                <label className="block sm:col-span-2">
                                    <Label testid="contact-label-message">
                                        Message / project scope
                                    </Label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        rows={4}
                                        required
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="What are you working on?"
                                        className={`${inputCls} resize-none`}
                                    />
                                </label>
                            </div>
                            <button
                                data-testid="contact-form-submit"
                                type="submit"
                                disabled={sending}
                                className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-olive px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-olive-deep disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <>
                                        Send request
                                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                    </>
                                )}
                            </button>
                        </form>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

export default Contact;
